/**
 * Percento aggregator — builds the stacked-bar data for AnalyticsScreen.
 *
 * Filters transactions by mode (expense / income) and period (inclusive
 * start/end ISO dates). Foreign-currency transactions are FX-converted
 * into the primary currency via `convertMinor` before summing — there is
 * NO currency filter, so a JPY tx still counts when primary is TWD.
 *
 * Groups remaining transactions by category, sums (in primary currency),
 * sorts descending, and lumps anything past the top 6 into a single
 * "其他" slice. If there are 6 or fewer non-zero categories, `otherSlice`
 * is null — the bar simply renders all of them.
 */

import { convertMinor } from '@/lib/format/fx';
import { dayPart } from '@/lib/format/datetime';
import type { CategoryRow, IsoDate, TransactionRow, TxKind } from '@/lib/sheets/types';

export interface CategorySlice {
  category_id: string;
  total_minor: number;
  pct: number;
  color_token: string;
  name: string;
}

export interface PercentoResult {
  slices: CategorySlice[];
  otherSlice: CategorySlice | null;
  total_minor: number;
  /**
   * Currency the slices were aggregated in. Carried inside the result so
   * the renderer (`PercentoBar`) cannot display TWD slices with a USD
   * label — used to be a separate `currency` prop and the two could drift.
   */
  currency: string;
}

export interface BuildOpts {
  mode: 'expense' | 'income';
  period: { start: IsoDate; end: IsoDate };
  currency: string;
  /**
   * Cap on how many individual slices are returned; the rest gets lumped
   * into `otherSlice`. Defaults to 6 (matches HomeRightRail's compact
   * panel). Pass `Infinity` (used by AnalyticsScreen) to disable the
   * lump entirely so the caller can do its own <5%-threshold bucketing
   * into an expandable rollup — without this, the synthetic `__other__`
   * row landed in AnalyticsScreen's `major` group and rendered as an
   * un-expandable static row on the expense tab.
   */
  topN?: number;
}

const DEFAULT_TOP_N = 6;

export function buildPercento(
  transactions: TransactionRow[],
  categories: CategoryRow[],
  opts: BuildOpts,
): PercentoResult {
  const { mode, period, currency, topN = DEFAULT_TOP_N } = opts;
  const wantedKind: TxKind = mode;

  // v5: `t.date` may be `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`. Comparing the
  // raw string against `period.end` (always day-only) excludes any tx
  // with a time suffix on the boundary day, because lex `'2025-04-30T11:51'
  //  > '2025-04-30'`. Always compare via `dayPart()` to align both sides.
  const filtered = transactions.filter((t) => {
    if (t.deleted_at) return false;
    if (t.kind !== wantedKind) return false;
    const day = dayPart(t.date);
    return day >= period.start && day <= period.end;
  });

  if (filtered.length === 0) {
    return { slices: [], otherSlice: null, total_minor: 0, currency };
  }

  // Convert each tx to the primary currency before grouping. JPY/USD/etc.
  // contribute to the same slices once normalised — no per-currency filter.
  const sums = new Map<string, number>();
  let total_minor = 0;
  for (const tx of filtered) {
    const minor = convertMinor(tx.amount_minor, tx.currency || 'TWD', currency);
    sums.set(tx.category_id, (sums.get(tx.category_id) ?? 0) + minor);
    total_minor += minor;
  }

  const catLookup = new Map(categories.map((c) => [c.id, c]));
  const buildSlice = (id: string, total: number, fallbackName: string): CategorySlice => {
    const cat = catLookup.get(id);
    return {
      category_id: id,
      total_minor: total,
      pct: total_minor === 0 ? 0 : (total / total_minor) * 100,
      color_token: cat?.color_token ?? '--cat-other',
      name: cat?.name ?? fallbackName,
    };
  };

  const sorted = [...sums.entries()].sort((a, b) => b[1] - a[1]);

  if (sorted.length <= topN) {
    return {
      slices: sorted.map(([id, total]) => buildSlice(id, total, id)),
      otherSlice: null,
      total_minor,
      currency,
    };
  }

  const topSlices = sorted.slice(0, topN).map(([id, total]) => buildSlice(id, total, id));
  const restTotal = sorted
    .slice(topN)
    .reduce((s, [, total]) => s + total, 0);
  const otherSlice: CategorySlice = {
    category_id: '__other__',
    total_minor: restTotal,
    pct: total_minor === 0 ? 0 : (restTotal / total_minor) * 100,
    color_token: '--cat-other',
    name: '其他',
  };

  return { slices: topSlices, otherSlice, total_minor, currency };
}
